import CreatableSelect from "react-select/creatable";
import Select, { components } from 'react-select';
import { List, RowComponentProps } from "react-window";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";

function RowComponent({
    index,
    style,
    comp,
}: RowComponentProps<{
    comp: any,
}>) {
    return (
        <div style={style}>{comp[index]}</div>
    )
}

export default function SelectField({
    label,
    id,
    name,
    value,
    options,
    isRequired,
    isCreatable,
    isMulti,
    handleChange,
    menuPortalTarget,
    handleCreate,
    index,
    disabled,
    isLoading,
}: {
    label?: string,
    id: string,
    name: string,
    value: {value:string | number, label: string | number} | {value:string | number, label: string | number}[] | null,
    options: {value:string | number, label: string | number}[],
    isRequired: boolean,
    isCreatable: boolean,
    isMulti: boolean,
    handleChange: (e: {value:string | number, label: string | number} | {value:string | number, label: string | number}[], name: string, index?: number) => void,
    menuPortalTarget?: HTMLElement | null,
    handleCreate?: (value: string) => void,
    index?: number,
    disabled?: boolean,
    isLoading?: boolean,
}) {
    const theme = useTheme();
    const selectRef = useRef<HTMLDivElement | null>(null);
    const [ maxWidth, setMaxWidth ] = useState(200);

    useEffect(() => {
        if (selectRef.current) {
            const width = selectRef.current.offsetWidth;
            setMaxWidth(width - 24);
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
          if (selectRef.current) {
            setMaxWidth(selectRef.current.offsetWidth - 24);
          }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const MenuList = (props:any) => {
        const itemHeight = 37;
        const {  children, maxHeight } = props;
        // const [value] = getValue();
        // const initialOffset = options.indexOf(value) * itemHeight;
        const height = Math.min(maxHeight, children.length * itemHeight);
    
        return (
            <div style={{ height }}>
                <List
                    rowComponent={RowComponent}
                    rowCount={children.length}
                    rowHeight={itemHeight}
                    rowProps={{
                        comp: children,
                    }}
                />
            </div>
        )
    }
    
    const Option = (props: any) => {
        const { children } = props;
        return (
            <components.Option {...props}>
                <div style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: `${maxWidth}px`  // adjust as needed
                }}>
                    {children}
                </div>
            </components.Option>
        )
    }


    return (
        <div className="w-full flex flex-col justify-center items-start gap-2 text-default">
            {label !== undefined && <label htmlFor={id} className={`text-[12px] font-medium ${isRequired && 'required'}`}>{label}</label>}
            <div className="w-full" ref={selectRef}>
            {isCreatable ? (
                <CreatableSelect 
                    isDisabled={disabled}
                    menuPortalTarget={menuPortalTarget}
                    components={{ MenuList, Option }}
                    onCreateOption={handleCreate}
                    id={id} 
                    options={options}
                    isMulti={isMulti} 
                    value={value} 
                    onChange={(e:any) => handleChange(e, name, index)} 
                    className="w-full"
                    menuPlacement="auto"
                    styles={{
                        control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? theme.palette.primary.main : '#C1C7CD',
                            width: '100%',
                            minHeight: 44,
                            borderRadius: '8px',
                            boxShadow: 'none',
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                cursor: 'pointer'
                            }
                        }),
                        valueContainer: (baseStyles, state) => ({
                            ...baseStyles,
                            padding: '0 12px',
                        }),
                        menuList: (baseStyles, state) => ({
                            ...baseStyles,
                            color: '#35424A',
                        }),
                        menu: (baseStyles, state) => ({
                            ...baseStyles,
                            zIndex: 50,
                        }),
                        option: (baseStyles, state) => ({
                            ...baseStyles,
                            backgroundColor: state.isSelected ? theme.palette.secondary.main : state.isFocused ? theme.palette.secondary.light : 'white',
                            color: '#35424A',
                            '&:hover': {
                                cursor: 'pointer',
                            }
                        })
                    }}
                />
            ) : (
                <Select 
                    isDisabled={disabled}
                    isLoading={isLoading}
                    menuPortalTarget={menuPortalTarget}
                    id={id} 
                    options={options}
                    isMulti={isMulti} 
                    components={{ MenuList, Option }}
                    value={value} 
                    onChange={(e:any) => handleChange(e, name, index)} 
                    className="w-full"
                    menuPlacement="auto"
                    styles={{
                        control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? theme.palette.primary.main : '#C1C7CD',
                            width: '100%',
                            minHeight: 44,
                            borderRadius: '8px',
                            boxShadow: 'none',
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                cursor: 'pointer'
                            }
                        }),
                        valueContainer: (baseStyles, state) => ({
                            ...baseStyles,
                            padding: '0 12px',
                        }),
                        menuList: (baseStyles, state) => ({
                            ...baseStyles,
                            color: '#35424A'
                        }),
                        menu: (baseStyles, state) => ({
                            ...baseStyles,
                            zIndex: 50,
                        }),
                        option: (baseStyles, state) => ({
                            ...baseStyles,
                            backgroundColor: state.isSelected ? theme.palette.secondary.main : state.isFocused ? theme.palette.secondary.light : 'white',
                            color: '#35424A',
                            '&:hover': {
                                cursor: 'pointer',
                            }
                        })
                    }}
                />
            )}
            </div>
        </div>
    )
}